import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Typography,
  Avatar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';


import { providerIcons, nativeDecisionProviders, nativeDecisionInputKey } from './GlobalSettingUtils';

const ModelCard = ({ model, modelType, providerLabel, onEdit, onDelete, t }) => {
  const info = providerIcons[model.provider] || providerIcons['local'];
  const formattedMaxContext = (() => {
    const tokenValue = Number(model.max_context_tokens);
    const legacyCharacterValue = Number(model.max_context_chars);
    const value = Number.isFinite(tokenValue) && tokenValue > 0
      ? tokenValue
      : (Number.isFinite(legacyCharacterValue) && legacyCharacterValue > 0
        ? Math.max(Math.floor(legacyCharacterValue / 4), 1)
        : null);
    return Number.isFinite(value) && value > 0 ? value.toLocaleString() : null;
  })();
  const formattedMaxTokens = (() => {
    const value = Number(model.max_tokens);
    return Number.isFinite(value) && value > 0 ? value.toLocaleString() : null;
  })();
  const reasoningEffort = model.reasoning_effort && model.reasoning_effort !== 'none'
    ? String(model.reasoning_effort)
    : null;
  const logprobsEnabled = model.logprobs_enabled === undefined || model.logprobs_enabled === null
    ? true
    : Boolean(model.logprobs_enabled);
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e0e0e0',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease'
      }}
    >
      <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar 
            src={info.icon}
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: 'transparent',
              mr: 1.5
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {providerLabel || model.provider}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                lineHeight: 1.2,
                mt: 0.25,
                fontSize: '0.7rem'
              }}
            >
              {info.description}
            </Typography>
          </Box>
        </Box>
        
        {model.model_name && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
            <strong>{t('global_setting.model_name', 'Model')}:</strong> {model.model_name}
          </Typography>
        )}

        {model.provider === 'openrouter' && model.openrouter_provider && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
            <strong>{t('global_setting.openrouter_provider', 'OpenRouter provider')}:</strong> {model.openrouter_provider}
          </Typography>
        )}

        {(modelType === 'text' || modelType === 'decision') && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t(nativeDecisionProviders.has(model.provider) ? 'global_setting.decision_native' : 'global_setting.decision_generated')}
          </Typography>
        )}
        {nativeDecisionProviders.has(model.provider) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t(nativeDecisionInputKey(model.provider))}
          </Typography>
        )}
        {formattedMaxContext && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
            <strong>{t('global_setting.max_context_tokens', 'Max context (tokens)')}:</strong> {formattedMaxContext}
          </Typography>
        )}
        {formattedMaxTokens && !nativeDecisionProviders.has(model.provider) && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
            <strong>{t('global_setting.max_tokens', 'Max tokens')}:</strong> {formattedMaxTokens}
          </Typography>
        )}
        {reasoningEffort && !nativeDecisionProviders.has(model.provider) && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
            <strong>{t('global_setting.reasoning_effort', 'Reasoning')}:</strong> {reasoningEffort}
          </Typography>
        )}
        {!nativeDecisionProviders.has(model.provider) && <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
          <strong>{t('global_setting.logprobs_enabled', 'Token logprobs')}:</strong>{' '}
          {logprobsEnabled
            ? t('global_setting.logprobs_enabled_value', 'Enabled')
            : t('global_setting.logprobs_disabled_value', 'Disabled')}
        </Typography>}
        
        {model.has_api_token && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
            <strong>{t('global_setting.api_token', 'API Token')}:</strong> ***
          </Typography>
        )}
        {model.has_api_secret && (
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
            <strong>{t('global_setting.api_secret', 'API Secret')}:</strong> ***
          </Typography>
        )}
        
        {model.api_endpoint && (
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mb: 0.5, 
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={model.api_endpoint}
          >
            <strong>{t('global_setting.endpoint', 'Endpoint')}:</strong> {model.api_endpoint}
          </Typography>
        )}
      </CardContent>
      
      <CardActions sx={{ pt: 0, px: 2, pb: 1 }}>
        <Button 
          size="small" 
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          onClick={() => onEdit(model, modelType)}
          sx={{ fontSize: '0.75rem', py: 0.5 }}
        >
          {t('global_setting.edit', 'Edit')}
        </Button>
        <Button 
          size="small" 
          color="error"
          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          onClick={() => onDelete(model, modelType)}
          sx={{ fontSize: '0.75rem', py: 0.5 }}
        >
          {t('global_setting.delete_model', 'Delete Model')}
        </Button>
      </CardActions>
    </Card>
  );
};

export default ModelCard;
